import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, MessageCircleHeart, PenLine, Headphones, Check } from "lucide-react";

const Cta = ({ label, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center justify-center rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-8 py-4 text-base font-medium transition-colors shadow-sm"
  >
    {label}
  </button>
);

const PILLARS = [
  [BookOpen, "Read every translation, free", "NIV, KJV, WEB, ASV, NKJV, AMP — plus a Strong's Hebrew & Greek concordance. The text stays the center."],
  [MessageCircleHeart, "A virtual study companion that goes deeper", "Ask questions and study a passage with a Scripture-anchored guide that points you back to the Word — never replacing it."],
  [Headphones, "Listen on the go", "For those times when reading isn't possible."],
  [PenLine, "Capture thoughts as they come", "Record a voice note anytime — it's transcribed and tidied into a titled note you can turn into a study."],
];

const TIERS = [
  ["Free", "$0", "Read freely, plus 15 study credits a month for Go Deeper."],
  ["Basic", "$7", "100 study credits a month, unlimited Go Deeper conversations."],
  ["Pro", "$16.99", "The largest monthly credit pool, plus the full suite of creation tools."],
];

export default function Welcome() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const verseRef = params.get("ref") || "";
  const verseText = params.get("verse") || "";
  const go = () => {
    if (verseRef) {
      try {
        localStorage.setItem("owp_intended_study", JSON.stringify({ input: verseRef, mode: "passage" }));
      } catch (e) { /* ignore */ }
    }
    navigate("/study");
  };

  return (
    <div className="min-h-screen bg-[#07090E] text-slate-200" data-testid="welcome-funnel">
      {/* Header */}
      <header className="border-b border-[#1E273D] bg-[#0E131F]/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="text-xl text-white">OpenWord AI</span>
          <button onClick={go} data-testid="welcome-nav-start" className="text-sm font-medium text-[#00E5FF] hover:text-[#00B8CC]">
            Start free
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-16 text-center">
        {verseRef ? (
          <div data-testid="welcome-nugget-continuity" className="mb-8 rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-5 max-w-xl mx-auto">
            {verseText && <p className="text-lg italic text-slate-200 leading-relaxed">&ldquo;{verseText}&rdquo;</p>}
            <p className="text-xs font-medium uppercase tracking-widest text-[#00E5FF] mt-2">{verseRef}</p>
          </div>
        ) : null}
        <p className="text-xs uppercase tracking-[0.2em] text-[#00E5FF]">A Scripture-first virtual study companion</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] mt-5">
          {verseRef ? (<>You&rsquo;ve read the verse.<br />Now open it up.</>) : (<>Open the Word,<br />and go deeper.</>)}
        </h1>
        <p className="text-base sm:text-lg text-slate-400 mt-6 max-w-xl mx-auto leading-relaxed">
          {verseRef
            ? "Create a free account and today's verse becomes a full guided study — read it in every translation, look up the original words, and talk it through."
            : "Read Scripture, sit with it, and talk it through — a quiet space that keeps the text at the center and helps you understand it, verse by verse."}
        </p>
        <div className="mt-9">
          <Cta label={verseRef ? "Study this free — no card needed" : "Start free — no card needed"} testid="welcome-hero-cta" onClick={go} />
          <p className="text-xs text-slate-500 mt-3">7-day full-access trial · then a free plan forever · cancel anytime</p>
        </div>
      </section>

      {/* Heart */}
      <section className="bg-[#10222A] border-y border-[#00E5FF]/10">
        <div className="max-w-2xl mx-auto px-6 py-12 text-center">
          <blockquote className="text-xl sm:text-2xl italic text-slate-200 leading-relaxed">
            “The app is a door to the text — not a substitute for it.”
          </blockquote>
          <p className="text-sm text-slate-400 mt-4">
            OpenWord AI exists to help you meet Scripture for yourself: honestly, unhurried, and open to anyone.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl sm:text-4xl text-white text-center leading-tight">
          Everything you need to study well.
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {PILLARS.map(([Icon, title, body], i) => (
            <div key={i} data-testid={`welcome-pillar-${i}`} className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6">
              <div className="w-11 h-11 rounded-xl bg-[#10222A] flex items-center justify-center text-[#00E5FF]">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl text-white mt-5">{title}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing peek */}
      <section className="bg-[#0E131F] border-y border-[#1E273D]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl sm:text-4xl text-white text-center leading-tight">
            Start free. Grow if it helps.
          </h2>
          <p className="text-center text-sm text-slate-400 mt-3">Reading Scripture is always free. Paid plans just add more room and more tools.</p>
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {TIERS.map(([name, price, desc], i) => (
              <div key={i} data-testid={`welcome-tier-${i}`} className={`rounded-2xl border p-6 ${i === 2 ? "border-[#00E5FF] bg-[#07090E]" : "border-[#1E273D]"}`}>
                <div className="flex items-baseline justify-between">
                  <span className="text-lg text-white">{name}</span>
                  <span className="text-sm text-slate-400">{price}<span className="text-xs text-slate-500">{i === 0 ? "" : "/mo"}</span></span>
                </div>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Cta label="Start free" testid="welcome-pricing-cta" onClick={go} />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h2 className="text-4xl sm:text-5xl text-white leading-tight">Open the Word today.</h2>
        <p className="text-base text-slate-400 mt-5 max-w-md mx-auto leading-relaxed">
          Bring one passage or one question. We&rsquo;ll help you sit with it.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <Cta label="Start free — no card needed" testid="welcome-final-cta" onClick={go} />
          <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Free plan forever</span>
            <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1E273D] py-8 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} OpenWord AI · A door to the Word
      </footer>
    </div>
  );
}
